import {create} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import {safeLocalStorage} from '../../../util/localStorage';
import {
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_HISTORY_SIDEBAR_WIDTH,
} from '../constants';

const wbAgentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: name => safeLocalStorage.getItem(name),
  setItem: (name, value) => {
    try {
      safeLocalStorage.setItem(name, value);
    } catch (e) {
      if (
        e instanceof DOMException &&
        (e.code === 22 || e.name === 'QuotaExceededError')
      ) {
        console.warn(`localStorage quota exceeded for "${name}"`, e);
        return;
      }
      console.warn(`Failed to persist "${name}" to localStorage`, e);
    }
  },
  removeItem: name => safeLocalStorage.removeItem(name),
};

/**
 * RestoreTarget is the thread to reopen on the next page load, stamped with
 * the viewer who left it open.
 */
export type RestoreTarget = {
  /** threadId of the committed thread to reopen. */
  threadId: string;
  /** viewerId of the viewer who left the thread open. */
  viewerId: string;
};

interface WBAgentWindowState {
  // Agent chat open state
  open: boolean;
  /** restoreTarget names the thread to reopen on load; null stays closed. */
  restoreTarget: RestoreTarget | null;
  // Drawer width
  drawerWidth: number;
  historySidebarOpen: boolean;
  historySidebarWidth: number;

  // Actions
  setOpen: (open: boolean) => void;
  setRestoreTarget: (target: RestoreTarget | null) => void;
  setDrawerWidth: (width: number) => void;
  setHistorySidebarOpen: (open: boolean) => void;
  setHistorySidebarWidth: (width: number) => void;
}

interface PersistedWBAgentWindowPreferences {
  restoreTarget: RestoreTarget | null;
  drawerWidth: number;
  historySidebarOpen: boolean;
  historySidebarWidth: number;
}

interface LegacyPersistedWBAgentWindowPreferences extends Partial<PersistedWBAgentWindowPreferences> {
  open?: boolean;
  restoreThreadId?: string | null;
  docked?: boolean;
  windowPosition?: {x: number; y: number} | null;
  windowWidth?: number;
  windowHeight?: number;
}

/**
 * useWBAgentWindowStore holds the WBAgent chat's window state, persisted to
 * localStorage.
 */
export const useWBAgentWindowStore = createWBAgentWindowStore();

/**
 * createWBAgentWindowStore builds a WBAgent chat window store persisted to
 * localStorage. Components use the shared {@link useWBAgentWindowStore}.
 */
/* oxlint-disable wandb/zustand-enforce-slices-when-large-state -- flat window settings store where slicing adds no value */
export function createWBAgentWindowStore() {
  return create<WBAgentWindowState>()(
    devtools(
      persist<WBAgentWindowState, [], [], PersistedWBAgentWindowPreferences>(
        (set, get) => ({
          open: false,
          restoreTarget: null,
          drawerWidth: DEFAULT_DRAWER_WIDTH,
          historySidebarOpen: false,
          historySidebarWidth: DEFAULT_HISTORY_SIDEBAR_WIDTH,

          setOpen: open => set(open ? {open} : {open, restoreTarget: null}),
          setRestoreTarget: target => {
            // NOTE: Skip an unchanged target so persist does not rewrite storage.
            if (isSameRestoreTarget(get().restoreTarget, target)) {
              return;
            }
            set({restoreTarget: target});
          },
          setDrawerWidth: width => set({drawerWidth: width}),
          setHistorySidebarOpen: historySidebarOpen =>
            set({historySidebarOpen}),
          setHistorySidebarWidth: historySidebarWidth =>
            set({historySidebarWidth}),
        }),
        {
          name: 'wb-agent-window-preferences',
          storage: createJSONStorage(() => wbAgentStorage),
          version: 3,
          migrate: persistedState => {
            const state =
              persistedState as LegacyPersistedWBAgentWindowPreferences | null;

            return {
              restoreTarget: null,
              drawerWidth: state?.drawerWidth ?? DEFAULT_DRAWER_WIDTH,
              historySidebarOpen: state?.historySidebarOpen ?? false,
              historySidebarWidth:
                state?.historySidebarWidth ?? DEFAULT_HISTORY_SIDEBAR_WIDTH,
            };
          },
          partialize: state => ({
            restoreTarget: state.restoreTarget,
            drawerWidth: state.drawerWidth,
            historySidebarOpen: state.historySidebarOpen,
            historySidebarWidth: state.historySidebarWidth,
          }),
        }
      ),
      {name: 'WBAgentWindowStore'}
    )
  );
}

function isSameRestoreTarget(
  a: RestoreTarget | null,
  b: RestoreTarget | null
): boolean {
  if (a === b) {
    return true;
  }
  return (
    a != null &&
    b != null &&
    a.threadId === b.threadId &&
    a.viewerId === b.viewerId
  );
}
